import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, Check, CircleAlert, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { DataState, formatMoney, formatTime, PageHeader } from "../../components/ui";
import { localDateTimeToUnix, toLocalDateTimeInput, validateUnixRange } from "../../utils/time";

const DAY = 24 * 60 * 60;

export function OverviewPage() {
  const [search, setSearch] = useSearchParams();
  const now = useMemo(() => Math.floor(Date.now() / 1000), []);
  const startAt = numberParam(search, "start_at", now - DAY);
  const endAt = numberParam(search, "end_at", now + 1);
  const [timeDraft, setTimeDraft] = useState({ start_at: String(startAt), end_at: String(endAt) });
  const [customTimeOpen, setCustomTimeOpen] = useState(() => !presetDays(startAt, endAt));
  useEffect(() => {
    if (search.has("start_at") && search.has("end_at")) return;
    const next = new URLSearchParams(search);
    next.set("start_at", String(startAt));
    next.set("end_at", String(endAt));
    setSearch(next, { replace: true });
  }, [endAt, search, setSearch, startAt]);
  useEffect(() => {
    setTimeDraft({ start_at: String(startAt), end_at: String(endAt) });
    setCustomTimeOpen(!presetDays(startAt, endAt));
  }, [startAt, endAt]);
  const summary = useQuery({
    queryKey: ["dashboard-summary", startAt, endAt],
    queryFn: () => api.dashboard(startAt, endAt),
    refetchInterval: 30_000,
  });
  const circuits = useQuery({ queryKey: ["circuits"], queryFn: () => api.circuits(), refetchInterval: 10_000 });
  const data = summary.data?.data;
  const successRate = data?.request_count ? (data.request_success_count / data.request_count) * 100 : 0;
  const attention = (circuits.data?.data || []).filter((item) => item.state !== "closed" || item.channel_status === "disabled");
  const activePresetDays = customTimeOpen ? 0 : presetDays(startAt, endAt);
  const timeDirty = timeDraft.start_at !== String(startAt) || timeDraft.end_at !== String(endAt);
  const draftStartAt = Number(timeDraft.start_at);
  const draftEndAt = Number(timeDraft.end_at);
  const timeError = validateUnixRange(draftStartAt, draftEndAt, 366);
  const rangeLabel = activePresetDays === 1 ? "过去 24 小时" : activePresetDays ? `过去 ${activePresetDays} 天` : `${formatTime(startAt)} 至 ${formatTime(endAt)}`;
  const detailsURL = `/manage/costs?start_at=${startAt}&end_at=${endAt}&granularity=${endAt - startAt > 7 * DAY ? "day" : "hour"}`;
  const usePreset = (days: number) => {
    const current = Math.floor(Date.now() / 1000);
    const range = { start_at: String(current - days * DAY), end_at: String(current + 1) };
    setTimeDraft(range);
    setCustomTimeOpen(false);
    setSearch(range, { replace: true });
  };
  const updateTimeDraft = (field: "start_at" | "end_at", value: string) => {
    setTimeDraft((current) => ({ ...current, [field]: value }));
  };
  const applyTime = () => setSearch({ start_at: timeDraft.start_at, end_at: timeDraft.end_at }, { replace: true });

  return (
    <>
      <PageHeader
        title="运行概览"
        description={`${rangeLabel}的调用、成本与当前需要处理的渠道状态。`}
        actions={<button className="secondary-button" disabled={summary.isFetching || circuits.isFetching} onClick={() => { summary.refetch(); circuits.refetch(); }}><RefreshCw size={16} />{summary.isFetching || circuits.isFetching ? "刷新中" : "刷新"}</button>}
      />
      <section className="overview-range-bar">
        <div className="filter-choice-group"><span>统计时间</span><div className="segmented-control" aria-label="概览时间范围">
          {[{ label: "24 小时", days: 1 }, { label: "7 天", days: 7 }, { label: "30 天", days: 30 }].map((item) => <button type="button" key={item.days} aria-pressed={activePresetDays === item.days} className={activePresetDays === item.days ? "active" : ""} onClick={() => usePreset(item.days)}>{item.label}</button>)}
          <button type="button" aria-pressed={customTimeOpen} className={customTimeOpen ? "active" : ""} onClick={() => setCustomTimeOpen(true)}>自定义</button>
        </div></div>
        {customTimeOpen ? <div className="custom-time-range">
          <label className="compact-field"><span>开始时间</span><input type="datetime-local" value={toLocalDateTimeInput(draftStartAt)} onInput={(event) => updateTimeDraft("start_at", localDateTimeToUnix(event.currentTarget.value))} /></label>
          <span className="range-separator">至</span>
          <label className="compact-field"><span>结束时间</span><input type="datetime-local" value={toLocalDateTimeInput(draftEndAt)} onInput={(event) => updateTimeDraft("end_at", localDateTimeToUnix(event.currentTarget.value))} /></label>
          <button type="button" className={timeDirty && !timeError ? "primary-button" : "secondary-button"} disabled={!timeDirty || Boolean(timeError)} onClick={applyTime}><Check size={15} />{timeError || "应用时间"}</button>
        </div> : null}
        <div className="overview-range-value"><CalendarDays size={15} />{formatTime(startAt)} 至 {formatTime(endAt)}</div>
      </section>
      <DataState loading={summary.isLoading} error={summary.error}>
        <section className="metric-band">
          <Metric label="请求量" value={(data?.request_count || 0).toLocaleString()} hint={`${data?.attempt_count || 0} 次渠道尝试`} />
          <Metric label="成功率" value={`${successRate.toFixed(1)}%`} hint={`${data?.request_failure_count || 0} 次失败`} />
          <Metric label="模型成本" value={formatMoney(data?.total_cost || "0")} hint="按渠道实际计费" />
          <Metric label="异常渠道" value={String(data?.open_circuit_count || 0)} hint="OPEN / HALF_OPEN" tone={data?.open_circuit_count ? "danger" : "normal"} />
          <Metric label="数据更新" value={formatTime(data?.data_updated_at || 0)} hint="小时汇总口径" compact />
        </section>
      </DataState>

      <div className="overview-grid">
        <section className="content-section">
          <div className="section-heading"><div><h2>调用与成本</h2><p>{rangeLabel}，按小时汇总</p></div><Link to={detailsURL}>查看明细 <ArrowRight size={15} /></Link></div>
          <div className="outcome-summary" aria-label="请求结果构成">
            <div className="outcome-total"><span>总请求</span><strong>{(data?.request_count || 0).toLocaleString()}</strong><small>{rangeLabel}</small></div>
            <div className="outcome-bars">
              <div><span><i className="success-key" />成功</span><strong>{(data?.request_success_count || 0).toLocaleString()}</strong></div>
              <div className="bar-track"><span className="success-bar" style={{ width: `${successRate}%` }} /></div>
              <div><span><i className="failure-key" />失败</span><strong>{(data?.request_failure_count || 0).toLocaleString()}</strong></div>
              <div className="bar-track"><span className="failure-bar" style={{ width: `${100 - successRate}%` }} /></div>
            </div>
          </div>
        </section>

        <section className="content-section attention-section">
          <div className="section-heading"><div><h2>需要处理</h2><p>优先展示熔断和暂停渠道</p></div><Link to="/manage/circuits">全部状态 <ArrowRight size={15} /></Link></div>
          <DataState loading={circuits.isLoading} error={circuits.error} empty={!attention.length} emptyTitle="全部渠道状态正常" emptyDescription="当前没有熔断、探测中或暂停的渠道">
            <div className="attention-list">
              {attention.slice(0, 6).map((item) => (
                <Link to={`/manage/circuits?channel_id=${item.channel_id}`} key={item.channel_id} className="attention-row">
                  <CircleAlert size={17} />
                  <div><strong>{item.channel_name}</strong><span>{item.model_key} · {item.last_error_kind || item.state}</span></div>
                  <span>{item.state === "open" ? "熔断" : item.channel_status === "disabled" ? "暂停" : "探测"}</span>
                </Link>
              ))}
            </div>
          </DataState>
        </section>
      </div>
    </>
  );
}

function numberParam(params: URLSearchParams, key: string, fallback: number) {
  const value = Number(params.get(key));
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function presetDays(startAt: number, endAt: number) {
  const range = endAt - startAt;
  return [1, 7, 30].find((days) => Math.abs(range - days * DAY) < 120) || 0;
}

function Metric({ label, value, hint, tone = "normal", compact = false }: {
  label: string;
  value: string;
  hint: string;
  tone?: "normal" | "danger";
  compact?: boolean;
}) {
  return <div className={`metric ${tone} ${compact ? "compact" : ""}`}><span>{label}</span><strong>{value}</strong><small>{hint}</small></div>;
}
